/**
 * Scale & send confirm handler logic.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';

import {
  filterSenderNamesMax11,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { applyScheduleInterval } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import type { ShortLinkTld } from '@/hooks';
import type { CreatePreparedDropPayload } from '@/hooks/smswiser-campaign-workspace/mutations';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { resolveLeadsListId, resolveRouteId, resolveTldValue, resolveWiserId } from '../resolvers';

export interface ScaleConfirmPayload {
  workDate: string;
  titleBase?: string;
  titlePrefix?: string;
  routeId: number;
  leadsListWiserId: number;
  shortLinkTldValue: string;
  amount: number;
  executeAfter?: string;
  senderNames: string[];
  smsTexts: string[];
  forceSend?: boolean;
  batchAmount?: number;
  batchDelay?: number;
  useOnlyNewDomains?: boolean;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
  staticTagId?: string | null;
  scheduleIntervalMinutes?: number;
  routeIdOverride?: number | null;
  shortLinkTldOverride?: string | null;
  senderNamesOverride?: string[] | null;
  smsTextsOverride?: string[] | null;
  senderNamesOverridePerDrop?: string[][];
  smsTextsOverridePerDrop?: string[][];
}

export interface ScaleConfirmDeps {
  countryKey: string;
  campaigns: Array<{ id: number; name: string }>;
  primaryWiserId: number;
  leadsLists: { id: number; name: string }[];
  routes: { id: number; name: string }[];
  tlds: ShortLinkTld[];
  creativeSmsTexts: string[] | undefined;
  updateCreative: { mutateAsync: (data: { smsTexts: string[] }) => Promise<unknown> };
  createPreparedFlex: {
    mutateAsync: (opts: {
      countryKey: string;
      payload: CreatePreparedDropPayload;
      wiserId: number;
    }) => Promise<{ id: string }>;
  };
  sendPreparedFlex: {
    mutateAsync: (opts: {
      countryKey: string;
      preparedDropId: string;
      wiserId: number;
    }) => Promise<unknown>;
  };
  toastError: (title: string, msg: string) => void;
}

export async function executeScaleConfirm(
  payload: ScaleConfirmPayload,
  multiRows: DropsWithStatsRow[] | undefined,
  deps: ScaleConfirmDeps,
): Promise<void> {
  const rows = multiRows && multiRows.length > 0 ? multiRows : null;
  if (!rows) {
    deps.toastError('Scale & send failed', 'No drop selected.');
    throw new Error('No drop selected');
  }

  const isSingleRow = rows.length === 1;
  const allTextsFromRows = rows.flatMap((r) =>
    Array.isArray(r.drop.smsText)
      ? r.drop.smsText.filter((t): t is string => typeof t === 'string')
      : r.drop.smsText ? [String(r.drop.smsText)] : [],
  );
  const perDropTexts = payload.smsTextsOverridePerDrop;
  const perDropSenders = payload.senderNamesOverridePerDrop;
  const textsToMerge = isSingleRow
    ? payload.smsTexts
    : perDropTexts != null && perDropTexts.length > 0
      ? perDropTexts.flat()
      : payload.smsTextsOverride != null && payload.smsTextsOverride.length > 0
        ? payload.smsTextsOverride
        : allTextsFromRows;
  const mergedForDb = uniqueKeepOrderStrings([...(deps.creativeSmsTexts ?? []), ...textsToMerge])
    .filter((t) => String(t).trim().length > 0)
    .slice(0, 50);
  await deps.updateCreative.mutateAsync({ smsTexts: mergedForDb });

  const intervalMin = payload.scheduleIntervalMinutes ?? 0;
  const baseTime = payload.executeAfter ?? new Date().toISOString();

  for (let idx = 0; idx < rows.length; idx++) {
    const row = rows[idx];
    const wiserId = deps.campaigns.length > 0 ? resolveWiserId(row.drop.campaign, deps.campaigns) : deps.primaryWiserId;
    if (wiserId == null || wiserId <= 0) {
      deps.toastError('Scale & send failed', `Could not resolve campaign for drop "${row.drop.title}".`);
      throw new Error(`Unknown campaign for drop: ${row.drop.title}`);
    }
    const leadsListWiserId = isSingleRow ? payload.leadsListWiserId : resolveLeadsListId(row.drop.leadsList, deps.leadsLists);
    if (leadsListWiserId == null || leadsListWiserId <= 0) {
      deps.toastError('Scale & send failed', `Could not resolve leads list for drop: ${row.drop.title}`);
      throw new Error(`Unknown leads list: ${row.drop.leadsList}`);
    }
    const routeId = isSingleRow
      ? payload.routeId
      : (payload.routeIdOverride != null && payload.routeIdOverride > 0 ? payload.routeIdOverride : resolveRouteId(row.drop.smsGatewayRoute, deps.routes));
    if (routeId == null || routeId <= 0) {
      deps.toastError('Scale & send failed', `No gateway for drop: ${row.drop.title}. Select a gateway to apply to all.`);
      throw new Error(`Unknown gateway for drop: ${row.drop.title}`);
    }
    const shortLinkTldValue = isSingleRow
      ? payload.shortLinkTldValue
      : (payload.shortLinkTldOverride != null && payload.shortLinkTldOverride.trim() !== ''
          ? payload.shortLinkTldOverride.trim()
          : resolveTldValue(row.drop.shortLinkTld, deps.tlds));
    const rawSenderNames = isSingleRow
      ? payload.senderNames
      : (perDropSenders != null && idx < perDropSenders.length && perDropSenders[idx]?.length > 0
          ? perDropSenders[idx].slice(0, 50)
          : payload.senderNamesOverride != null && payload.senderNamesOverride.length > 0
            ? payload.senderNamesOverride.slice(0, 50)
            : Array.isArray(row.drop.senderName)
              ? row.drop.senderName.filter((s): s is string => typeof s === 'string').slice(0, 50)
              : row.drop.senderName ? [String(row.drop.senderName)] : []);
    const senderNames = filterSenderNamesMax11(rawSenderNames);
    const smsTexts = isSingleRow
      ? payload.smsTexts
      : (perDropTexts != null && idx < perDropTexts.length && perDropTexts[idx]?.length > 0
          ? perDropTexts[idx].slice(0, 50)
          : payload.smsTextsOverride != null && payload.smsTextsOverride.length > 0
            ? payload.smsTextsOverride.slice(0, 50)
            : Array.isArray(row.drop.smsText)
              ? row.drop.smsText.filter((t): t is string => typeof t === 'string').slice(0, 50)
              : row.drop.smsText ? [String(row.drop.smsText)] : []);
    if (senderNames.length === 0 || smsTexts.length === 0) {
      deps.toastError('Scale & send failed', `Drop "${row.drop.title}" has no sender or SMS text.`);
      throw new Error(`Missing sender/text for drop: ${row.drop.title}`);
    }
    const dropExecuteAfter = intervalMin > 0 && idx > 0
      ? applyScheduleInterval(baseTime, intervalMin, idx)
      : payload.executeAfter;
    const rowStaticTagId = payload.staticTagId ?? row.drop.staticTag?.id;
    const createPayload: CreatePreparedDropPayload = {
      workDate: payload.workDate,
      routeId,
      leadsListWiserId,
      shortLinkTldValue,
      amount: payload.amount,
      executeAfter: dropExecuteAfter,
      senderNames: filterSenderNamesMax11(senderNames),
      smsTexts,
      forceSend: payload.forceSend,
      batchAmount: payload.batchAmount,
      batchDelay: payload.batchDelay,
      useOnlyNewDomains: payload.useOnlyNewDomains,
      clickIdStructure: payload.clickIdStructure,
      domainScheme: payload.domainScheme,
      ...(rowStaticTagId != null && { staticTagId: rowStaticTagId }),
    };
    const created = await deps.createPreparedFlex.mutateAsync({
      countryKey: deps.countryKey,
      payload: createPayload,
      wiserId,
    });
    await deps.sendPreparedFlex.mutateAsync({
      countryKey: deps.countryKey,
      preparedDropId: created.id,
      wiserId,
    });
  }
}
